/**
 * Configuration centralisée des langues supportées par la plateforme
 *
 * SOURCE UNIQUE DE VÉRITÉ pour toutes les informations relatives aux langues.
 * Utilisé par config-lang.js, useMultiLanguage, LanguageSelector, et autres.
 *
 * ⚠️ IMPORTANT : Cette configuration doit être synchronisée avec
 * /backend/src/config/languages.js pour garantir la cohérence backend/frontend.
 *
 * @module locales/languageConfig
 * @author Équipe Brydge
 * @since 2.0.0
 */

/**
 * Configuration complète de chaque langue supportée
 * Contient toutes les métadonnées nécessaires pour l'affichage et la gestion des langues
 *
 * @constant {Object<string, Object>}
 * @property {string} code - Code ISO 639-1 de la langue (fr, en, nl, de)
 * @property {string} label - Nom de la langue dans sa langue native
 * @property {string} icon - Icône Iconify du drapeau (flagpack:*)
 * @property {string} flag - Emoji du drapeau (pour affichages alternatifs)
 */
export const LANGUAGES = {
  fr: {
    code: 'fr',
    label: 'Français',
    icon: 'flagpack:fr',
    flag: '🇫🇷',
  },
  en: {
    code: 'en',
    label: 'English',
    icon: 'flagpack:gb-nir',
    flag: '🇬🇧',
  },
  nl: {
    code: 'nl',
    label: 'Nederlands',
    icon: 'flagpack:nl',
    flag: '🇳🇱',
  },
  de: {
    code: 'de',
    label: 'Deutsch',
    icon: 'flagpack:de',
    flag: '🇩🇪',
  },
};

/**
 * Liste ordonnée des codes de langues supportées
 * Ordre : Français (défaut) > Anglais > Néerlandais > Allemand
 *
 * ⚠️ SYNCHRONISATION BACKEND : Doit correspondre à SUPPORTED_LANGUAGES
 * dans /backend/src/config/languages.js
 *
 * @constant {string[]}
 */
export const SUPPORTED_LANGUAGE_CODES = ['fr', 'en', 'nl', 'de'];

/**
 * Langue par défaut de la plateforme
 * Utilisée comme fallback quand aucune langue n'est spécifiée
 *
 * ⚠️ SYNCHRONISATION BACKEND : Doit correspondre à DEFAULT_LANGUAGE
 * dans /backend/src/config/languages.js
 *
 * @constant {string}
 */
export const DEFAULT_LANGUAGE = 'fr';

/**
 * Récupère la configuration d'une langue par son code
 *
 * @param {string} code - Code de langue (fr, en, nl, de)
 * @return {Object|null} Configuration de la langue ou null si non trouvée
 *
 * @example
 * getLanguageConfig('fr')
 * // => { code: 'fr', label: 'Français', icon: 'flagpack:fr', flag: '🇫🇷' }
 */
export const getLanguageConfig = (code) => {
  return LANGUAGES[code] || null;
};

/**
 * Vérifie si un code de langue est supporté
 *
 * @param {string} code - Code de langue à vérifier
 * @return {boolean} True si la langue est supportée
 *
 * @example
 * isLanguageSupported('fr') // => true
 * isLanguageSupported('es') // => false
 */
export const isLanguageSupported = (code) => {
  return SUPPORTED_LANGUAGE_CODES.includes(code);
};

/**
 * Récupère toutes les configurations de langues sous forme de tableau
 * Utile pour l'affichage dans les sélecteurs ou listes
 *
 * @return {Array<Object>} Tableau des configurations de langues
 *
 * @example
 * getAllLanguages()
 * // => [
 * //   { code: 'fr', label: 'Français', icon: 'flagpack:fr', flag: '🇫🇷' },
 * //   { code: 'en', label: 'English', icon: 'flagpack:gb-nir', flag: '🇬🇧' },
 * //   ...
 * // ]
 */
export const getAllLanguages = () => {
  return SUPPORTED_LANGUAGE_CODES.map((code) => LANGUAGES[code]);
};

/**
 * Résout les langues qu'un établissement propose réellement à ses clients.
 *
 * Si le multilingue est désactivé, l'établissement n'opère que dans sa langue
 * par défaut. Sinon, on retient ses langues activées, dans l'ordre canonique de
 * la plateforme. Ne renvoie jamais un tableau vide (fallback langue par défaut).
 *
 * @param {Object} [multiLanguage] - Config multiLanguage de l'établissement
 * @param {boolean} [multiLanguage.enabled] - Multilingue actif
 * @param {string} [multiLanguage.defaultLanguage] - Langue par défaut
 * @param {string[]} [multiLanguage.enabledLanguages] - Langues activées
 * @return {string[]} Codes de langues supportés proposés par l'établissement
 */
export const getCompanyLanguageCodes = (multiLanguage) => {
  const defaultLanguage = SUPPORTED_LANGUAGE_CODES.includes(multiLanguage?.defaultLanguage) ?
    multiLanguage.defaultLanguage :
    DEFAULT_LANGUAGE;

  if (!multiLanguage?.enabled) {
    return [defaultLanguage];
  }

  const enabled = Array.isArray(multiLanguage.enabledLanguages) ? multiLanguage.enabledLanguages : [];
  const ordered = SUPPORTED_LANGUAGE_CODES.filter((code) => enabled.includes(code));

  return ordered.length ? ordered : [defaultLanguage];
};

/**
 * Choisit la langue de PDF par défaut pour un achat de bon cadeau.
 *
 * Priorité à la langue de l'acheteur si l'établissement la propose, sinon à la
 * langue par défaut de l'établissement, enfin à sa première langue proposée.
 * La langue de l'acheteur est ramenée à un code court (« en-GB » → « en »).
 *
 * @param {Object} [multiLanguage] - Config multiLanguage de l'établissement
 * @param {string} [buyerLanguage] - Langue courante de l'acheteur
 * @return {string} Code de langue proposé par l'établissement
 */
export const resolveDefaultPdfLanguage = (multiLanguage, buyerLanguage) => {
  const codes = getCompanyLanguageCodes(multiLanguage);
  const base = (buyerLanguage || '').split('-')[0];

  if (codes.includes(base)) return base;
  if (codes.includes(multiLanguage?.defaultLanguage)) return multiLanguage.defaultLanguage;
  return codes[0];
};
